import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { HI, HI_PATTERNS } from "./hi";

const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = new Set(["SCRIPT", "STYLE", "TEXTAREA", "CODE", "INPUT"]);

function translateText(text) {
  const key = text.trim();
  if (!key || key.length > 400) return null;
  let v = HI[key];
  if (!v) {
    for (const [re, fn] of HI_PATTERNS) {
      const m = key.match(re);
      if (m) {
        v = fn(m);
        break;
      }
    }
  }
  return v ? text.replace(key, v) : null;
}

function skipNode(el) {
  return !el || SKIP.has(el.nodeName) || el.closest?.("[data-no-translate]");
}

function translateNode(node) {
  if (node.nodeType === 3) {
    if (skipNode(node.parentElement) || node.nodeValue === node.__hi) return;
    const out = translateText(node.nodeValue);
    if (out) {
      node.__en = node.nodeValue;
      node.__hi = out;
      node.nodeValue = out;
    }
    return;
  }
  if (node.nodeType !== 1 || skipNode(node) && node.nodeName !== "INPUT" && node.nodeName !== "TEXTAREA") return;
  ATTRS.forEach((a) => {
    const val = node.getAttribute?.(a);
    if (!val || node[`__hi_${a}`] === val) return;
    const out = translateText(val);
    if (out) {
      node[`__en_${a}`] = val;
      node[`__hi_${a}`] = out;
      node.setAttribute(a, out);
    }
  });
  if (skipNode(node)) return;
  node.childNodes.forEach(translateNode);
}

function restoreNode(node) {
  if (node.nodeType === 3) {
    if (node.__en && node.nodeValue === node.__hi) node.nodeValue = node.__en;
    node.__hi = undefined;
    return;
  }
  if (node.nodeType !== 1) return;
  ATTRS.forEach((a) => {
    if (node[`__en_${a}`] && node.getAttribute(a) === node[`__hi_${a}`]) node.setAttribute(a, node[`__en_${a}`]);
    node[`__hi_${a}`] = undefined;
  });
  node.childNodes.forEach(restoreNode);
}

function useDomTranslation(lang) {
  useEffect(() => {
    document.documentElement.lang = lang;
    if (lang !== "hi") {
      restoreNode(document.body);
      return undefined;
    }
    translateNode(document.body);
    const obs = new MutationObserver((muts) => {
      muts.forEach((m) => {
        if (m.type === "characterData") translateNode(m.target);
        else if (m.type === "attributes") translateNode(m.target);
        else m.addedNodes.forEach(translateNode);
      });
    });
    obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    return () => obs.disconnect();
  }, [lang]);
}

const I18nCtx = createContext({ lang: "en", t: (s) => s, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("schola_lang") || "en");
  const setLang = useCallback((l) => {
    localStorage.setItem("schola_lang", l);
    setLangState(l);
  }, []);
  const t = useCallback((s) => (lang === "hi" ? HI[s] || s : s), [lang]);
  useDomTranslation(lang);
  return <I18nCtx.Provider value={{ lang, setLang, t }}>{children}</I18nCtx.Provider>;
}

export const useT = () => useContext(I18nCtx);
